import {
    IPhysicalSensorModelSdResponse,
    SensorStandard,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export const ThirdPartySensorModelsDataSourceKey = 'third-party-sensor-models';
const ThirdPartySensorModelsDefaultSort: DataSourceSort[] = [
    { field: 'Manufacturer.Name', dir: 'asc' },
];

export type ThirdPartySensorsFilter = Readonly<{
    corporationId?: string;
    manufacturerId?: string;
    modelNumber?: string;
    sensorStandard?: SensorStandard;
    isApproved?: boolean;
    nameSearch?: string;
}>;

export type UseThirdPartySensorModelsDataSourceOptions = BaseDataSourceOptions<
    IPhysicalSensorModelSdResponse,
    ThirdPartySensorsFilter
>;

export type ThirdPartySensorModelsPagedDataSource = PagedDataSource<
    IPhysicalSensorModelSdResponse,
    ThirdPartySensorsFilter
>;

export type ThirdPartySensorModelsInfiniteDataSource = InfiniteDataSource<
    IPhysicalSensorModelSdResponse,
    ThirdPartySensorsFilter
>;

const getAllThirdPartySensorModels: DataSourceQuery<
    IPhysicalSensorModelSdResponse,
    ThirdPartySensorsFilter
> = ({
    filter: {
        modelNumber,
        sensorStandard,
        isApproved,
        manufacturerId,
        corporationId,
        nameSearch,
    },
    sort,
    page,
    pageLimit,
}) => {
    const filterExpr = Filter.and(
        modelNumber !== undefined ? Filter.eq('ModelNumber', modelNumber) : undefined,
        sensorStandard !== undefined
            ? Filter.eq('SensorStandard', sensorStandard)
            : undefined,
        manufacturerId !== undefined
            ? Filter.eq('Manufacturer.Id', manufacturerId)
            : undefined,
        Filter.or(
            corporationId
                ? Filter.eq('Corporation.CorporationId', corporationId)
                : undefined,
            isApproved ? Filter.isNull('Corporation') : undefined,
        ),
        isApproved === false ? Filter.isNotNull('Corporation') : undefined,
    );
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.sdGetPhysicalSensorModels({
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        }),
    );
};

export function usePagedThirdPartySensorModelsDataSource({
    defaultSort = ThirdPartySensorModelsDefaultSort,
    ...options
}: UseThirdPartySensorModelsDataSourceOptions): ThirdPartySensorModelsPagedDataSource {
    return usePagedDataSource({
        defaultSort,
        ...options,
        query: getAllThirdPartySensorModels,
    });
}

export function useInfiniteThirdPartySensorModelsDataSource({
    defaultSort = ThirdPartySensorModelsDefaultSort,
    ...options
}: UseThirdPartySensorModelsDataSourceOptions): ThirdPartySensorModelsInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort,
        ...options,
        query: getAllThirdPartySensorModels,
    });
}

export function invalidateThirdPartySensorModelsDataSource() {
    queryClient.invalidateQueries({
        queryKey: [ThirdPartySensorModelsDataSourceKey],
    });
}
